import * as React from 'react';
import { t } from '@reos/i18n';
import { Link } from '@tanstack/react-router';
import { V4Group, V4State, V4Switch } from '../ux-v4.js';
import type {
  DraftPatch,
  ListingDescriptionDraft,
  ListingDraftView,
  ListingTranslation,
  ListingTranslationLanguage,
  ListingVideo,
  ListingVideoList,
  ListingVideoMusicMood,
  ListingVideoOrientation,
  PublishResult,
} from '../../lib/listing-draft-api.js';
import { PrintCard } from './PrintCard.js';
import { useLeaveWarning } from '../../lib/leave-guard.js';
import type { PropertyMediaItem } from '../../lib/properties.js';
import { mediaSrc } from '../../lib/properties.js';
import { checkText, decisionText, fieldLabel, formatValue, issueText, portalName } from '../../lib/listing-draft-format.js';

/**
 * Krok UXV4b bod 2–3 — „Zveřejnit" (predloha `Zverejnit.png`).
 *
 * Text z overenych udaju (sablona; s AI pres kredity/klic kancelare), fotky
 * v poradi galerie nemovitosti („Vyfotit další" = nahrani do galerie),
 * prepinace portalu (Sreality, RealityMIX; ostatni „připravujeme napojení").
 * Hlavni tlacitko „Zveřejnit na N portálech". Co se skutecne odeslalo, rika
 * az server (rezim prostredi + brana §105) — obrazovka to jen poctive ukaze.
 */

const cs = { locale: 'cs' } as const;
const VISIBLE_PHOTOS = 3;

export function PublishStep({
  view,
  media,
  onSave,
  onGenerateText,
  onDescribe,
  onTranslate,
  onSocialCaption,
  onTakePhoto,
  onMoveFirst,
  onSetCaption,
  onPublish,
  onFixFields,
  publishing,
  result,
  aiError,
  videos,
  onCreateVideo,
}: {
  readonly view: ListingDraftView;
  /** Fotky galerie s nahledem (GET /v1/properties/:id/media), poradi = poradi inzeratu. */
  readonly media: readonly PropertyMediaItem[];
  readonly onSave: (patch: DraftPatch) => Promise<void>;
  readonly onGenerateText: (mode: 'template' | 'ai') => Promise<void>;
  /** Krok UI1 — AI popis inzerátu (SR4): návrh do pole, makléř upraví/potvrdí. `null` = chyba (hlásí `aiError`). */
  readonly onDescribe: () => Promise<ListingDescriptionDraft | null>;
  /** Krok NAP1 bod 2 — překlad inzerátu (AI). `null` = chyba (hlásí `aiError`). */
  readonly onTranslate: (language: ListingTranslationLanguage) => Promise<ListingTranslation | null>;
  /** Krok NAP1 bod 3 — text pro sociální sítě. `null` = chyba (hlásí `aiError`). */
  readonly onSocialCaption: () => Promise<string | null>;
  readonly onTakePhoto: (files: readonly File[]) => Promise<void>;
  readonly onMoveFirst: (mediaId: string) => Promise<void>;
  /**
   * Krok NOC3 bod 2 (nalez N19) — popisek fotky pro portaly
   * (`PATCH /v1/properties/:id/media/:mediaId`). `undefined` = nemovitost
   * jeste neni znama, pole se nevykresli.
   */
  readonly onSetCaption?: ((mediaId: string, caption: string) => Promise<void>) | undefined;
  readonly onPublish: () => void;
  readonly onFixFields: () => void;
  readonly publishing: boolean;
  readonly result: PublishResult | null;
  readonly aiError: string | null;
  /**
   * Krok UI1b — stav krátkých videí z fotek. `undefined` = obrazovka stav
   * ještě nenačetla (nebo starší server cestu nemá), blok se nekreslí.
   */
  readonly videos?: ListingVideoList | undefined;
  readonly onCreateVideo?:
    | ((orientation: ListingVideoOrientation, musicMood: ListingVideoMusicMood | null) => Promise<void>)
    | undefined;
}): React.JSX.Element {
  const [editing, setEditing] = React.useState(false);
  /**
   * Krok UXP4b bod 3 (nalez P2-8) — KLIKACI NAHLED INZERATU. Do UXP4b byl
   * „Náhled" jen STITEK rezimu prostredi (nekam nevedl) a makléř nemel jak
   * videt, co z inzeratu vznikne, nez ho odesle. Nahled stoji na TYCHZ datech,
   * ktera jdou na portal (`view.title`, `view.text`, `view.fields`, fotky),
   * takze nemuze ukazat nic jineho, nez co se odesle.
   */
  const [preview, setPreview] = React.useState(false);
  const [draftText, setDraftText] = React.useState(view.text.value);
  React.useEffect(() => setDraftText(view.text.value), [view.text.value]);
  /*
   * Krok APP2 bod 4 (bod 25) — ODCHOD Z ROZEPSANEHO INZERATU. Text inzeratu
   * se uklada az klepnutim na „Hotovo" (`onSave({ text })` vyse), takze
   * obnoveni stranky rozepsany popis zahodi. Hlidka (`lib/leave-guard.ts`)
   * bezi presne po tu dobu, kdy se rozepsany text lisi od ulozeneho.
   */
  useLeaveWarning(editing && draftText !== view.text.value);
  const [describing, setDescribing] = React.useState(false);
  const [flaggedNumbers, setFlaggedNumbers] = React.useState<readonly string[] | null>(null);
  /*
   * Krok NAB5 bod 3 — DVE VARIANTY, MAKLER VYBERE A UPRAVI. Jedna varianta
   * (starsi chovani UI1) jde dal rovnou do pole; teprve dve a vic se nabidnou
   * k vyberu, aby se jednoduche „Navrhnout popis" nezmenilo na formular.
   */
  const [variants, setVariants] = React.useState<readonly string[]>([]);
  /*
   * Krok NAP1 bod 2–3 — PREKLAD A TEXT PRO SITE ZIJI JEN NA OBRAZOVCE.
   *
   * Ani jedno se neuklada ke konceptu a nic z toho nejde na portal: preklad
   * je pro cizojazycneho klienta, text pro site si makler zkopiruje. Proto
   * jsou to stavy komponenty, ne pole pohledu.
   */
  const [translation, setTranslation] = React.useState<ListingTranslation | null>(null);
  const [translating, setTranslating] = React.useState<ListingTranslationLanguage | null>(null);
  const [caption, setCaption] = React.useState<string | null>(null);
  const [captionCopied, setCaptionCopied] = React.useState(false);
  const fileInput = React.useRef<HTMLInputElement | null>(null);

  /**
   * Krok UXD4 (nalez V12 UX auditu 21. 10.) — „Napsat s AI" MUSI DAT VEDET,
   * ze pracuje. Zive zmerene volani `POST /v1/cases/<id>/listing-draft/text`
   * trvalo 37,3 s a tlacitko se po celou dobu nijak nezmenilo; makléř, ktery
   * pres pul minuty nevidi odezvu, klikne znovu — a druhy pozadavek nic
   * nebranilo poslat. Stav je tentyz vzor jako `describing` u „Navrhnout
   * popis" o kousek nize, jen pro obe varianty textu (AI i sablona).
   */
  const [generating, setGenerating] = React.useState<'template' | 'ai' | null>(null);

  async function handleGenerateText(mode: 'template' | 'ai'): Promise<void> {
    if (generating !== null) return;
    setGenerating(mode);
    try {
      await onGenerateText(mode);
    } finally {
      setGenerating(null);
    }
  }

  async function handleDescribe(): Promise<void> {
    setDescribing(true);
    setFlaggedNumbers(null);
    setVariants([]);
    try {
      const draft = await onDescribe();
      if (draft === null) return;
      const texts = draft.texts ?? (draft.text === null ? [] : [draft.text]);
      if (texts.length > 1) setVariants(texts);
      const first = texts[0] ?? draft.text;
      if (first !== null && first !== undefined) {
        setDraftText(first);
        setEditing(true);
      }
      if (draft.flagged) setFlaggedNumbers(draft.needsReviewNumbers);
    } finally {
      setDescribing(false);
    }
  }

  function useVariant(text: string): void {
    setDraftText(text);
    setEditing(true);
  }

  async function handleTranslate(language: ListingTranslationLanguage): Promise<void> {
    setTranslating(language);
    setTranslation(null);
    try {
      setTranslation(await onTranslate(language));
    } finally {
      setTranslating(null);
    }
  }

  async function handleSocialCaption(): Promise<void> {
    setCaptionCopied(false);
    setCaption(await onSocialCaption());
  }

  async function copyCaption(text: string): Promise<void> {
    // Starsi prohlizec (a jsdom) `clipboard` nema — tlacitko pak jen oznami, ze
    // text je k rucnimu zkopirovani v poli, misto aby obrazovka spadla.
    await navigator.clipboard?.writeText(text);
    setCaptionCopied(true);
  }

  /*
   * Krok NAP1 bod 1 — VAROVANI O POLOZE V NAHLEDU EXPORTU. Kontrolu pocita
   * server (`checkDraft`, AI5) z volby „Zobrazit přesnou adresu"; obrazovka
   * ji jen rika tam, kde makler kouka na to, co z inzeratu vznikne.
   */
  const locationCheck = (view.exportWarnings ?? []).find((check) => check.code === 'precise_location_consent_missing') ?? null;

  /** Krok PORT6: limit popisu ze serveru (jeden zdroj pravdy), ne z UI. */
  const textMaxLength = view.text.maxLength;

  const selected = view.portals.filter((portal) => portal.availability === 'connected' && portal.selected).length;
  const isPreview = view.envMode === 'DEMO';
  const shown = media.slice(0, VISIBLE_PHOTOS);
  const more = media.length - shown.length;
  const failed = result !== null && !result.ok ? result : null;
  const done = result !== null && result.ok ? result : null;

  return (
    <div className="rh-ld-step" data-testid="ld-step-publish">
      <div className="rh-ld-hrow">
        <h2 className="rh-ld-h">{t('webAdmin.listingDraft.publish.textHeading', cs)}</h2>
        <button
          type="button"
          className="rh-ld-link"
          onClick={() => {
            if (editing) void onSave({ text: draftText }).then(() => setEditing(false));
            else setEditing(true);
          }}
          data-testid="ld-text-edit"
        >
          {editing ? t('webAdmin.listingDraft.publish.done', cs) : t('webAdmin.listingDraft.publish.edit', cs)}
        </button>
      </div>
      {editing ? (
        <textarea
          className="rh-ld-text rh-ld-text--edit"
          value={draftText}
          rows={7}
          aria-label={t('webAdmin.listingDraft.publish.textHeading', cs)}
          onChange={(event) => setDraftText(event.target.value)}
          data-testid="ld-text-input"
        />
      ) : (
        <section className="rh-ld-text" data-testid="ld-text">
          {view.text.value}
        </section>
      )}
      {/*
       * Krok PORT6 — POCITADLO ZNAKU. Limit NEOPISUJEME: posila ho server
       * (`text.maxLength`) z limitu napojenych portalu (Sreality potvrdila
       * 5000 znaku 2. 11. 2026). Starsi server pole neposila -> pocitadlo
       * se nekresli, misto aby ukazovalo vymyslene cislo.
       */}
      {textMaxLength !== undefined ? (
        <p className="rh-ld-foot" data-testid="ld-text-count">
          {t('webAdmin.listingDraft.publish.textCount', {
            ...cs,
            values: {
              count: String((editing ? draftText : view.text.value).length),
              max: String(textMaxLength),
            },
          })}
        </p>
      ) : null}
      <p className="rh-ld-foot" data-testid="ld-text-note">
        {t(`webAdmin.listingDraft.publish.textNote.${view.text.source}`, cs)}
      </p>
      <div className="rh-ld-text-acts">
        {view.text.aiAvailable ? (
          <button
            type="button"
            className="rh-v4-btn rh-v4-btn--tinted"
            disabled={generating !== null}
            aria-busy={generating === 'ai'}
            onClick={() => void handleGenerateText('ai')}
            data-testid="ld-text-ai"
          >
            {generating === 'ai'
              ? t('webAdmin.listingDraft.publish.aiWriting', cs)
              : t('webAdmin.listingDraft.publish.aiWrite', cs)}
          </button>
        ) : (
          <span className="rh-ld-foot" data-testid="ld-text-ai-off">
            {t('webAdmin.listingDraft.publish.aiUnavailable', cs)}
          </span>
        )}
        {view.text.source !== 'template' ? (
          <button
            type="button"
            className="rh-v4-btn rh-v4-btn--plain"
            disabled={generating !== null}
            onClick={() => void handleGenerateText('template')}
            data-testid="ld-text-template"
          >
            {t('webAdmin.listingDraft.publish.template', cs)}
          </button>
        ) : null}
        <button
          type="button"
          className="rh-v4-btn rh-v4-btn--tinted"
          disabled={describing}
          onClick={() => void handleDescribe()}
          data-testid="ld-text-describe"
        >
          {t('webAdmin.listingDraft.publish.aiDescribe', cs)}
        </button>
      </div>
      <p className="rh-ld-foot" data-testid="ld-text-describe-note">
        {t('webAdmin.listingDraft.publish.aiDescribeNote', cs)}
      </p>
      {aiError !== null ? (
        <p className="rh-ld-error" role="alert">
          {aiError}
        </p>
      ) : null}
      {variants.length > 1 ? (
        <section className="rh-ld-variants" data-testid="ld-text-variants">
          <p className="rh-ld-foot">{t('webAdmin.listingDraft.publish.aiVariants', cs)}</p>
          {variants.map((variant, index) => (
            <button
              key={variant}
              type="button"
              className="rh-v4-btn rh-v4-btn--plain rh-ld-variant"
              onClick={() => useVariant(variant)}
              data-testid={`ld-text-variant-${String(index + 1)}`}
            >
              {t('webAdmin.listingDraft.publish.aiVariantUse', { ...cs, values: { n: index + 1 } })}
              <span className="rh-ld-variant__text">{variant}</span>
            </button>
          ))}
        </section>
      ) : null}
      {flaggedNumbers !== null ? (
        <p className="rh-ld-error" role="alert" data-testid="ld-text-describe-flagged">
          {t('webAdmin.listingDraft.publish.aiDescribeFlagged', { ...cs, values: { numbers: flaggedNumbers.join(', ') } })}
        </p>
      ) : null}

      {/*
        Krok NAP1 bod 2–3 — PREKLAD PRO KLIENTA A TEXT PRO SITE.
        Obe veci stoji u textu inzeratu, protoze z nej vychazeji. Nic z toho
        se neodesila: preklad jde do pole, ktere makler upravi a posle sam,
        text pro site si zkopiruje.
      */}
      <h2 className="rh-ld-h rh-ld-h--gap">{t('webAdmin.listingDraft.publish.translateHeading', cs)}</h2>
      <div className="rh-ld-text-acts">
        <button
          type="button"
          className="rh-v4-btn rh-v4-btn--plain"
          disabled={translating !== null}
          onClick={() => void handleTranslate('en')}
          data-testid="ld-translate-en"
        >
          {t('webAdmin.listingDraft.publish.translateEn', cs)}
        </button>
        <button
          type="button"
          className="rh-v4-btn rh-v4-btn--plain"
          disabled={translating !== null}
          onClick={() => void handleTranslate('de')}
          data-testid="ld-translate-de"
        >
          {t('webAdmin.listingDraft.publish.translateDe', cs)}
        </button>
        <button
          type="button"
          className="rh-v4-btn rh-v4-btn--plain"
          onClick={() => void handleSocialCaption()}
          data-testid="ld-social"
        >
          {t('webAdmin.listingDraft.publish.social', cs)}
        </button>
      </div>
      <p className="rh-ld-foot" data-testid="ld-translate-note">
        {t('webAdmin.listingDraft.publish.translateNote', cs)}
      </p>
      {translation !== null && translation.text !== null ? (
        <textarea
          className="rh-ld-text rh-ld-text--edit"
          defaultValue={translation.text}
          key={`${translation.language}-${translation.text}`}
          rows={7}
          aria-label={t('webAdmin.listingDraft.publish.translationLabel', cs)}
          data-testid="ld-translation"
        />
      ) : null}
      {translation !== null && translation.text === null ? (
        <p className="rh-ld-error" role="alert" data-testid="ld-translation-unusable">
          {t('webAdmin.listingDraft.publish.translateUnusable', cs)}
        </p>
      ) : null}
      {caption !== null ? (
        <div data-testid="ld-social-result">
          <textarea
            className="rh-ld-text rh-ld-text--edit"
            defaultValue={caption}
            key={caption}
            rows={7}
            aria-label={t('webAdmin.listingDraft.publish.socialLabel', cs)}
            data-testid="ld-social-caption"
          />
          <p className="rh-ld-foot">{t('webAdmin.listingDraft.publish.socialNote', cs)}</p>
          <button
            type="button"
            className="rh-v4-btn rh-v4-btn--plain"
            onClick={() => void copyCaption(caption)}
            data-testid="ld-social-copy"
          >
            {t(
              captionCopied ? 'webAdmin.listingDraft.publish.socialCopied' : 'webAdmin.listingDraft.publish.socialCopy',
              cs,
            )}
          </button>
        </div>
      ) : null}

      {/*
        Krok UI1b — KRÁTKÉ VIDEO Z FOTEK.
        Stojí u fotek a textu, protože z nich vzniká. Nikam se nezveřejňuje:
        makléř si hotové video STÁHNE a vloží sám (zadání). Render běží ve
        frontě workera, proto tady není „hotovo hned", ale stav.
      */}
      {videos !== undefined && onCreateVideo !== undefined ? (
        <VideoBlock videos={videos} photoCount={media.length} onCreateVideo={onCreateVideo} />
      ) : null}

      <div className="rh-ld-hrow rh-ld-hrow--gap">
        <h2 className="rh-ld-h" data-testid="ld-photos-count">
          {t('webAdmin.listingDraft.publish.photos', { ...cs, values: { count: media.length } })}
        </h2>
        <button type="button" className="rh-ld-link" onClick={() => fileInput.current?.click()} data-testid="ld-take-photo">
          {t('webAdmin.listingDraft.publish.takePhoto', cs)}
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          hidden
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            event.target.value = '';
            if (files.length > 0) void onTakePhoto(files);
          }}
          data-testid="ld-photo-input"
        />
      </div>
      {media.length === 0 ? (
        <p className="rh-ld-foot">{t('webAdmin.listingDraft.publish.noPhotos', cs)}</p>
      ) : (
        <section className="rh-ld-photos" data-testid="ld-photos">
          {shown.map((item, index) => (
            <button
              key={item.id}
              type="button"
              className="rh-ld-photo"
              style={{ backgroundImage: `url("${mediaSrc(item.thumbUrl)}")` }}
              aria-label={
                index === 0
                  ? t('webAdmin.listingDraft.publish.photoFirst', { ...cs, values: { caption: item.caption ?? '' } })
                  : t('webAdmin.listingDraft.publish.photoMakeFirst', { ...cs, values: { caption: item.caption ?? '' } })
              }
              onClick={() => (index === 0 ? undefined : void onMoveFirst(item.id))}
              data-testid={`ld-photo-${String(index)}`}
            >
              {item.caption !== null ? <span className="rh-ld-photo__cap">{item.caption}</span> : null}
            </button>
          ))}
          {more > 0 ? (
            <span className="rh-ld-photo rh-ld-photo--more" data-testid="ld-photos-more">
              +{more}
            </span>
          ) : null}
        </section>
      )}
      {/*
        Krok NOC3 bod 2 (nalez N19) — POPISEK FOTKY ZADAVA CLOVEK.
        Do NOC3 byl popiskem NAZEV NAHRANEHO SOUBORU (server ho ukladal do
        `property_media.caption`) a portaly ho posilaly jako verejny `alt` /
        `popis`. Upload uz popisek neplni; tady je pole, kde ho makler muze
        napsat — a popisek je POJMENOVANY jako verejny, ne jako nalepka
        miniatury. Ulozeni jde pres `PATCH …/media/:id` (`onSetCaption`).
      */}
      {media.length === 0 || onSetCaption === undefined ? null : (
        <div data-testid="ld-photo-captions">
          {shown.map((item, index) => (
            <PhotoCaptionField
              key={item.id}
              item={item}
              index={index}
              onSetCaption={onSetCaption}
            />
          ))}
        </div>
      )}
      <p className="rh-ld-foot">{t('webAdmin.listingDraft.publish.photosNote', cs)}</p>

      <h2 className="rh-ld-h">{t('webAdmin.listingDraft.publish.portalsHeading', cs)}</h2>
      {/*
        Krok UXP4b bod 3, oprava 1 (nález P2-8): počet napojených portálů se
        POJMENUJE. Na Portálech stojí v souhrnu „N z M zveřejněno" tentýž
        jmenovatel (`canPublishToPortal`), takže dvě obrazovky netvrdí
        o témže trhu jiné číslo.
      */}
      <p className="rh-ld-foot" data-testid="ld-portals-count">
        {t('webAdmin.listingDraft.publish.portalsConnectedCount', {
          ...cs,
          values: {
            connected: String(view.portals.filter((portal) => portal.availability === 'connected').length),
            total: String(view.portals.length),
          },
        })}
      </p>
      <V4Group testId="ld-portals">
        {view.portals.map((portal) => {
          const preparing = portal.availability === 'preparing';
          return (
            <div key={portal.id} className="rh-ld-portal" data-testid={`ld-portal-${portal.id}`}>
              <span className="rh-ld-portal__text">
                <span className="rh-ld-portal__name">{portalName(portal.id)}</span>
                <span className="rh-ld-portal__note">
                  {preparing
                    ? t('webAdmin.listingDraft.publish.portalNote.preparing', cs)
                    : t(`webAdmin.listingDraft.publish.portalNote.${portal.note}`, cs)}
                </span>
              </span>
              <V4Switch
                checked={!preparing && portal.selected}
                disabled={preparing}
                label={portalName(portal.id)}
                onChange={(next) => void onSave({ portals: { [portal.id]: next } })}
                testId={`ld-switch-${portal.id}`}
              />
            </div>
          );
        })}
      </V4Group>
      <p className="rh-ld-foot">{t('webAdmin.listingDraft.publish.portalsFootnote', cs)}</p>
      {/*
        Krok NAP1 bod 1 — PRESNA ADRESA JEN SE SOUHLASEM MAJITELE.
        Vypnuto je vychozi stav (i u inzeratu z doby pred NAP1): na portaly
        jde ulice a obec. Zapnuti je rozhodnuti cloveka a jde do auditu
        (`listing.draft.updated`, `showExactAddress`).
      */}
      <V4Group testId="ld-exact-address">
        <div className="rh-ld-portal">
          <span className="rh-ld-portal__text">
            <span className="rh-ld-portal__name">{t('webAdmin.listingDraft.publish.exactAddress', cs)}</span>
            <span className="rh-ld-portal__note">{t('webAdmin.listingDraft.publish.exactAddressNote', cs)}</span>
          </span>
          <V4Switch
            checked={view.showExactAddress === true}
            label={t('webAdmin.listingDraft.publish.exactAddress', cs)}
            onChange={(next) => void onSave({ showExactAddress: next })}
            testId="ld-switch-exact-address"
          />
        </div>
      </V4Group>
      <p className="rh-ld-foot" data-testid="ld-mode">
        {t(`webAdmin.listingDraft.publish.mode.${view.envMode}`, cs)}
      </p>

      {/*
        Krok VYV1 — „Tisk a PDF": vyloha do vylohy, letak s QR a list do
        sanonu z TECHZE udaju karty, ktere jdou na portaly (R78, zadne druhe
        zadavani). Zije u nabidky, protoze se tiskne ve chvili, kdy makler
        nabidku pousti do sveta.
      */}
      <PrintCard view={view} />

      {failed !== null ? (
        <section className="rh-ld-issues" role="alert" data-testid="ld-issues">
          <h2 className="rh-ld-issues__title">{t('webAdmin.listingDraft.publish.issuesTitle', cs)}</h2>
          <ul>
            {failed.blockingChecks.map((check) => (
              <li key={check.code}>
                {check.code === 'price_missing'
                  ? t('webAdmin.listingDraft.issue.missing_all', { ...cs, values: { field: fieldLabel('asking_price').toLowerCase() } })
                  : (checkText(check, view.propertyKind) ?? '')}
              </li>
            ))}
            {failed.missing.map((field) => (
              <li key={`missing-${field}`}>
                {t('webAdmin.listingDraft.issue.missing_all', { ...cs, values: { field: fieldLabel(field).toLowerCase() } })}
              </li>
            ))}
            {failed.portalIssues.map((issue) => (
              <li key={`${issue.portal}-${issue.code}-${issue.field}`} data-testid="ld-issue">
                {issueText(issue)}
              </li>
            ))}
          </ul>
          <button type="button" className="rh-v4-btn rh-v4-btn--tinted" onClick={onFixFields} data-testid="ld-fix-fields">
            {t('webAdmin.listingDraft.publish.fixAction', cs)}
          </button>
        </section>
      ) : null}

      {done !== null ? (
        <section className="rh-v4-group" data-testid="ld-result">
          {done.decisions
            .filter((decision) => decision.action !== 'skip_preparing')
            .map((decision) => (
              <div key={decision.portal} className="rh-ld-check" data-testid={`ld-result-${decision.portal}`}>
                {/* P1EXP1: odeslano = testovaci import i ostry export. */}
                <V4State
                  state={
                    (decision.action === 'send_test' || decision.action === 'send_live') &&
                    decision.problem === null
                      ? 'done'
                      : 'later'
                  }
                />
                <span>{decisionText(decision)}</span>
              </div>
            ))}
          <Link to="/portaly" className="rh-ld-more" data-testid="ld-result-portaly">
            {t('webAdmin.listingDraft.result.statusLink', cs)}
          </Link>
        </section>
      ) : null}

      <button
        type="button"
        className="rh-ld-more"
        aria-expanded={preview}
        onClick={() => setPreview((value) => !value)}
        data-testid="ld-preview-open"
      >
        {preview
          ? t('webAdmin.listingDraft.publish.previewHide', cs)
          : t('webAdmin.listingDraft.publish.previewShow', cs)}
      </button>
      {preview ? (
        <section className="rh-ld-preview" data-testid="ld-preview">
          <h3 className="rh-ld-preview__title" data-testid="ld-preview-title">{view.title}</h3>
          <p className="rh-ld-preview__params" data-testid="ld-preview-params">
            {view.fields
              .filter((field) => field.main && field.value !== null)
              .map((field) => `${fieldLabel(field.key)}: ${formatValue(field) ?? ''}`)
              .concat(
                view.fill
                  .filter((field) => field.value !== null)
                  .map((field) => `${fieldLabel(field.key)}: ${formatValue(field) ?? ''}`),
              )
              .join(' · ')}
          </p>
          {media[0] !== undefined ? (
            <span
              className="rh-ld-photo rh-ld-preview__photo"
              style={{ backgroundImage: `url("${mediaSrc(media[0].thumbUrl)}")` }}
              data-testid="ld-preview-photo"
            />
          ) : null}
          <p className="rh-ld-preview__text">{view.text.value}</p>
          {locationCheck !== null ? (
            <p className="rh-ld-error" role="alert" data-testid="ld-preview-location-warning">
              {checkText(locationCheck, view.propertyKind) ?? ''}
            </p>
          ) : null}
        </section>
      ) : null}

      <div className="rh-ld-cta">
        <button
          type="button"
          className="rh-v4-btn rh-v4-btn--filled rh-v4-btn--block rh-ld-cta__btn"
          disabled={selected === 0 || publishing}
          onClick={onPublish}
          data-testid="ld-publish"
        >
          {t(isPreview ? 'webAdmin.listingDraft.publish.previewCta' : 'webAdmin.listingDraft.publish.cta', {
            ...cs,
            values: { count: selected },
          })}
        </button>
        {isPreview ? (
          <span className="rh-v4-chip rh-v4-chip--progress rh-ld-cta__badge" data-testid="ld-preview-badge">
            {t('webAdmin.listingDraft.publish.previewBadge', cs)}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Krok NOC3 bod 2 (nalez N19) — POLE POPISKU JEDNE FOTKY.
 *
 * Uklada se az pri OPUSTENI pole (`blur`), ne pri psani: opozdena odpoved
 * PATCHe pri psani prepisuje rozepsany text (poučení z `DraftTextInput`
 * v `components/contracts.tsx`). Pole je neovladane a `key` je id fotky,
 * takze novy radek galerie prinese vlastni vychozi hodnotu bez efektu po
 * pripojeni (zavod UXR4).
 */
function PhotoCaptionField({
  item,
  index,
  onSetCaption,
}: {
  readonly item: PropertyMediaItem;
  readonly index: number;
  readonly onSetCaption: (mediaId: string, caption: string) => Promise<void>;
}): React.JSX.Element {
  const inputId = `ld-photo-caption-${String(index)}`;
  return (
    <div className="rh-ld-editor" data-testid={`ld-photo-caption-${String(index)}`}>
      <label className="rh-ld-foot" htmlFor={inputId}>
        {t('webAdmin.listingDraft.publish.photoCaptionLabel', cs)}
      </label>
      <input
        id={inputId}
        className="rh-input rh-ld-input"
        type="text"
        maxLength={300}
        defaultValue={item.caption ?? ''}
        placeholder={t('webAdmin.listingDraft.publish.photoCaptionPlaceholder', cs)}
        onBlur={(event) => {
          const next = event.target.value.trim();
          if (next === (item.caption ?? '')) return;
          void onSetCaption(item.id, next);
        }}
        data-testid={`ld-photo-caption-input-${String(index)}`}
      />
    </div>
  );
}

/**
 * Krok UI1b — BLOK KRÁTKÉHO VIDEA Z FOTEK.
 *
 * Dvě tlačítka = dvě orientace (Stories 9:16, YouTube 16:9), pod nimi stav
 * posledních žádostí. Hotové video je ODKAZ na stažení, ne přehrávač: soubor
 * patří makléři do telefonu, kde ho vloží do příspěvku.
 *
 * Důvod selhání přichází ze serveru jako KÓD (`listing_video.error.*`), ne
 * jako věta — hlášku skládá obrazovka z katalogu (CLAUDE.md §1 bod 5).
 * Neznámý důvod (např. text z ffmpegu) dostane obecnou větu, nikdy se
 * nevypisuje surově.
 */
const VIDEO_FAILURE_TEXT: Readonly<Record<string, string>> = {
  'listing_video.error.no_photos': 'webAdmin.listingDraft.publish.videoFailedNoPhotos',
  'listing_video.error.too_large': 'webAdmin.listingDraft.publish.videoFailedTooLarge',
};

/** Krok VID2 — popisek nálady hudby (katalog textů, CLAUDE.md §1 bod 5). */
const MUSIC_MOOD_TEXT: Readonly<Record<ListingVideoMusicMood, string>> = {
  KLIDNA: 'webAdmin.listingDraft.publish.musicMoodKlidna',
  SVIZNA: 'webAdmin.listingDraft.publish.musicMoodSvizna',
  ELEGANTNI: 'webAdmin.listingDraft.publish.musicMoodElegantni',
};

function videoFailureText(reason: string | null): string {
  const key = reason === null ? undefined : VIDEO_FAILURE_TEXT[reason];
  return t((key ?? 'webAdmin.listingDraft.publish.videoFailed') as Parameters<typeof t>[0], cs);
}

function VideoRow({ video }: { readonly video: ListingVideo }): React.JSX.Element {
  if (video.status === 'DONE' && video.downloadPath !== null) {
    return (
      <div className="rh-ld-check" data-testid={`ld-video-${video.id}`}>
        <V4State state="done" />
        <a className="rh-ld-more" href={video.downloadPath} data-testid={`ld-video-download-${video.id}`}>
          {t('webAdmin.listingDraft.publish.videoDownload', cs)}
        </a>
        <span className="rh-ld-foot" data-testid={`ld-video-meta-${video.id}`}>
          {t('webAdmin.listingDraft.publish.videoDone', {
            ...cs,
            values: {
              // Délka se ukazuje v celých sekundách — na „27,3 s" se nikdo neptá.
              seconds: String(Math.round((video.durationMs ?? 0) / 1000)),
              width: String(video.widthPx ?? 0),
              height: String(video.heightPx ?? 0),
              photos: String(video.photoCount ?? 0),
            },
          })}
        </span>
      </div>
    );
  }
  if (video.status === 'FAILED') {
    return (
      <div className="rh-ld-check" data-testid={`ld-video-${video.id}`}>
        <V4State state="later" />
        <span className="rh-ld-error" role="alert" data-testid={`ld-video-failed-${video.id}`}>
          {videoFailureText(video.failureReason)}
        </span>
      </div>
    );
  }
  return (
    <div className="rh-ld-check" data-testid={`ld-video-${video.id}`}>
      <V4State state="wait" />
      <span data-testid={`ld-video-waiting-${video.id}`}>
        {t(
          video.status === 'RUNNING'
            ? 'webAdmin.listingDraft.publish.videoRunning'
            : 'webAdmin.listingDraft.publish.videoPending',
          cs,
        )}
      </span>
    </div>
  );
}

function VideoBlock({
  videos,
  photoCount,
  onCreateVideo,
}: {
  readonly videos: ListingVideoList;
  readonly photoCount: number;
  readonly onCreateVideo: (orientation: ListingVideoOrientation, musicMood: ListingVideoMusicMood | null) => Promise<void>;
}): React.JSX.Element {
  const [creating, setCreating] = React.useState<ListingVideoOrientation | null>(null);
  const [musicMood, setMusicMood] = React.useState<ListingVideoMusicMood | null>(null);

  async function create(orientation: ListingVideoOrientation): Promise<void> {
    setCreating(orientation);
    try {
      await onCreateVideo(orientation, musicMood);
    } finally {
      setCreating(null);
    }
  }

  // Bez fotek není z čeho video složit a bez zbývajícího limitu by žádost
  // skončila 429 — v obou případech tlačítka nejdou zmáčknout a stojí u nich
  // důvod, místo aby makléř klikal do chyby.
  const limitReached = videos.remainingToday <= 0;
  const blocked = photoCount === 0 || limitReached;

  return (
    <>
      <h2 className="rh-ld-h rh-ld-h--gap">{t('webAdmin.listingDraft.publish.videoHeading', cs)}</h2>
      <p className="rh-ld-foot" data-testid="ld-video-note">
        {t('webAdmin.listingDraft.publish.videoNote', cs)}
      </p>
      {videos.availableMoods.length === 0 ? null : (
        <label className="rh-ld-foot" data-testid="ld-video-music">
          {t('webAdmin.listingDraft.publish.musicLabel', cs)}{' '}
          <select
            value={musicMood ?? ''}
            onChange={(event) => setMusicMood((event.target.value || null) as ListingVideoMusicMood | null)}
            data-testid="ld-video-music-select"
          >
            <option value="">{t('webAdmin.listingDraft.publish.musicNone', cs)}</option>
            {videos.availableMoods.map((mood) => (
              <option key={mood} value={mood}>
                {t(MUSIC_MOOD_TEXT[mood] as Parameters<typeof t>[0], cs)}
              </option>
            ))}
          </select>
        </label>
      )}
      <div className="rh-ld-text-acts">
        <button
          type="button"
          className="rh-v4-btn rh-v4-btn--tinted"
          disabled={blocked || creating !== null}
          onClick={() => void create('PORTRAIT')}
          data-testid="ld-video-portrait"
        >
          {t('webAdmin.listingDraft.publish.videoPortrait', cs)}
        </button>
        <button
          type="button"
          className="rh-v4-btn rh-v4-btn--plain"
          disabled={blocked || creating !== null}
          onClick={() => void create('LANDSCAPE')}
          data-testid="ld-video-landscape"
        >
          {t('webAdmin.listingDraft.publish.videoLandscape', cs)}
        </button>
      </div>
      <p className="rh-ld-foot" data-testid="ld-video-remaining">
        {t(
          limitReached
            ? 'webAdmin.listingDraft.publish.videoLimitReached'
            : 'webAdmin.listingDraft.publish.videoRemaining',
          { ...cs, values: { count: videos.remainingToday } },
        )}
      </p>
      {videos.items.length === 0 ? null : (
        <section className="rh-v4-group" data-testid="ld-videos">
          {videos.items.map((video) => (
            <VideoRow key={video.id} video={video} />
          ))}
        </section>
      )}
    </>
  );
}
